import { useQuery } from '@tanstack/react-query';
import { fetchWidgetData } from '../api/client';
import { normalizeWidgetType } from '../builder/widgetTypes';
import { getMockCompetitionData } from '../data/mockCompetitionData';

const STALE = {
  'match-card': 20_000,
  'match-info': 20_000,
  'goals-list': 20_000,
  'coach-card': 20_000,
  'live-scores': 20_000,
  'live-matches': 20_000,
  fixtures: 60_000,
  results: 60_000,
  'match-events': 20_000,
  lineups: 120_000,
  standings: 120_000,
  'group-standings': 120_000,
  'form-guide': 120_000,
  'team-stats': 120_000,
  'team-list': 120_000,
  'team-profile': 120_000,
  'team-card': 120_000,
  'player-list': 300_000,
  'player-profile': 300_000,
  'player-card': 300_000,
  'top-scorers': 300_000,
  'top-assists': 300_000,
  appearances: 300_000,
  minutes: 300_000,
  discipline: 300_000,
  injuries: 300_000,
};

const REFETCH_TYPES = new Set([
  'match-card',
  'match-info',
  'goals-list',
  'coach-card',
  'live-scores',
  'live-matches',
  'match-events',
]);

function mockPayload(type, mock, config) {
  const base = { competitionLabel: mock.label, _mock: true };
  switch (type) {
    case 'standings':
      return { ...base, table: mock.table, groups: mock.groups ?? null };
    case 'group-standings':
      return { ...base, groups: mock.groups ?? null };
    case 'form-guide':
      return { ...base, formRows: mock.formRows || [] };
    case 'team-stats':
      return { ...base, teamStats: mock.teamStats || [] };
    case 'team-list':
      return { ...base, teams: mock.teams || mock.table || [] };
    case 'team-profile':
    case 'team-card':
      return {
        ...base,
        teamProfile: {
          ...(mock.teamProfile || {
            team: 'Arsenal',
            position: 1,
            points: 38,
            played: 18,
            won: 12,
            drawn: 2,
            lost: 4,
            gf: 42,
            ga: 20,
            gd: 22,
            form: 'WWWDW',
          }),
          zone: 'ucl',
          zoneLabel: 'Champions League',
          zoneShort: 'UCL',
        },
      };
    case 'player-list': {
      const team = config?.selectedTeam;
      const squad = team && mock.squads?.[team] ? mock.squads[team] : [];
      return {
        ...base,
        players: squad,
        selectedTeam: team || null,
        listMode: 'squad',
      };
    }
    case 'player-profile':
    case 'player-card':
      return {
        ...base,
        playerProfile: mock.playerProfile || mock.scorers?.[0] || null,
      };
    case 'coach-card':
      return { ...base, featuredMatch: mock.featuredMatch };
    case 'top-scorers':
      return { ...base, scorers: mock.scorers || [] };
    case 'top-assists':
      return { ...base, assists: mock.assists || [] };
    case 'appearances':
      return { ...base, appearances: mock.appearances || [] };
    case 'minutes':
      return { ...base, minutes: mock.minutes || [] };
    case 'discipline':
      return { ...base, discipline: mock.discipline || [] };
    case 'injuries': {
      const all = mock.injuries || [];
      const team = config?.selectedTeam;
      return {
        ...base,
        injuries: team ? all.filter((r) => r.team === team) : all,
        selectedTeam: team || null,
      };
    }
    case 'match-events':
      return {
        ...base,
        featuredMatch: mock.featuredMatch,
        events: mock.events || mock.goals || [],
        goals: mock.goals || [],
      };
    case 'goals-list':
      return {
        ...base,
        featuredMatch: mock.featuredMatch,
        goals: mock.goals || mock.featuredMatch?.goals || [],
      };
    case 'match-info':
    case 'lineups':
      return {
        ...base,
        featuredMatch: mock.featuredMatch,
        lineups: mock.featuredMatch?.lineups || null,
        substitutions: mock.substitutions || [],
      };
    case 'fixtures':
      return { ...base, matches: mock.fixtures || mock.matches.filter((m) => m.upcoming) || [] };
    case 'results':
      return { ...base, matches: mock.results || mock.matches.filter((m) => m.finished) || [] };
    case 'live-matches':
      return { ...base, matches: mock.liveMatches || mock.matches.filter((m) => m.live) || [] };
    case 'live-scores':
      return { ...base, matches: mock.matches };
    default:
      return { ...base, featuredMatch: mock.featuredMatch, matches: mock.matches };
  }
}

export function useWidgetData(config, options = {}) {
  const { enabled = true } = options;
  const competitionId = config?.competitionId || 'pl';
  const type = normalizeWidgetType(config?.type || 'match-card');

  return useQuery({
    queryKey: [
      'widget-data',
      competitionId,
      type,
      config?.featuredMatchId || '',
      config?.selectedTeam || '',
      config?.selectedPlayer || '',
    ],
    queryFn: () =>
      fetchWidgetData(competitionId, type, {
        featuredMatchId: config?.featuredMatchId || null,
        selectedTeam: config?.selectedTeam || null,
        selectedPlayer: config?.selectedPlayer || null,
      }),
    enabled: Boolean(enabled && config),
    staleTime: STALE[type] ?? 30_000,
    refetchInterval: REFETCH_TYPES.has(type) ? 30_000 : false,
    retry: 1,
    placeholderData: () =>
      config?.featuredMatchId
        ? undefined
        : mockPayload(type, getMockCompetitionData(competitionId), config),
  });
}
